<script setup lang="ts">
import { inject, nextTick, ref, useTemplateRef, watch } from 'vue';
import NxDialog from './NxDialog.vue';
import { NxButton } from './../Button';
import { i18nInjectionKey } from '../internal';

const props = withDefaults(
  defineProps<{
    /** Will replace the i18n-placeholders, e.g. `"{entity} löschen"` as title */
    textReplacements: {
      entity?: string;
      entityArticle?: string;
      entities?: string;
    };
    /** Number of currently selected rows (0 = no selection, show single-delete dialog) */
    selectionCount?: number;
    /**
     * Put the focus on „Löschen“ when the dialog opens, so Enter deletes right
     * away — for a screen typed by keyboard that asks on a key (the posting
     * mask: F4 → Enter, BLP/nx#3198). Off by default: the focus is on
     * „Abbrechen“, and an Enter straight after opening never deletes (Enrico
     * 30.10.2026).
     */
    focusConfirm?: boolean;
  }>(),
  {
    selectionCount: 0,
    focusConfirm: false,
  }
);

const isDialogOpen = defineModel<boolean>('open', { required: true });
const confirmed = ref<boolean>(false);

const emit = defineEmits<{
  confirmed: [];
  canceled: [];
}>();

const i18nInjection = inject(i18nInjectionKey)!;

const primaryBtnRef = useTemplateRef('primary-btn-ref');
const cancelBtnRef = useTemplateRef('cancel-btn-ref');

watch(isDialogOpen, (isOpen) => {
  if (isOpen) confirmed.value = false;
});

/**
 * Once the dialog is shown, „Abbrechen“ takes the focus — „Löschen“ with
 * `focusConfirm`. Not one tick after `open`: the modal calls `showModal()`
 * later than that, and the browser then moved the focus to the first button of
 * the head (maximise).
 */
async function focusButton(): Promise<void> {
  await nextTick();
  const target = props.focusConfirm ? primaryBtnRef.value : cancelBtnRef.value;
  target?.focus({ focusVisible: true } as FocusOptions);
}
</script>

<template>
  <NxDialog
    v-model:open="isDialogOpen"
    :resizable="false"
    :title="
      props.selectionCount > 1
        ? i18nInjection.t('common.dialogs.delete.title_selection')
        : i18nInjection.t('common.dialogs.delete.title', {
            entity:
              props.textReplacements.entity ??
              i18nInjection.t('common.dialogs.delete.fallback.entity'),
          })
    "
    :description="
      props.selectionCount > 1
        ? i18nInjection.t('common.dialogs.delete.description_selection', {
            entities:
              props.textReplacements.entities ??
              i18nInjection.t('common.dialogs.delete.fallback.entities'),
          })
        : i18nInjection.t('common.dialogs.delete.description', {
            entityArticle:
              props.textReplacements.entityArticle ??
              i18nInjection.t('common.dialogs.delete.fallback.entity_article'),
            entity:
              props.textReplacements.entity ??
              i18nInjection.t('common.dialogs.delete.fallback.entity'),
          })
    "
    @modal-opened="focusButton"
    @modal-closed="
      () => {
        if (confirmed) {
          emit('confirmed');
        } else {
          emit('canceled');
        }
      }
    "
  >
    <template #buttons="{ close }">
      <NxButton
        v-if="props.selectionCount > 1"
        ref="primary-btn-ref"
        @click="
          () => {
            confirmed = true;
            close();
          }
        "
      >
        {{ i18nInjection.t('common.dialogs.delete.confirm_selection') }}
        {{ `(${props.selectionCount})` }}
      </NxButton>
      <NxButton
        v-else
        ref="primary-btn-ref"
        @click="
          () => {
            confirmed = true;
            close();
          }
        "
      >
        {{ i18nInjection.t('common.dialogs.delete.confirm') }}
      </NxButton>
      <NxButton
        ref="cancel-btn-ref"
        variant="outline"
        @click="
          () => {
            confirmed = false;
            close();
          }
        "
      >
        {{ i18nInjection.t('common.dialogs.delete.cancel') }}
      </NxButton>
    </template>
  </NxDialog>
</template>
